<template>
  <BaseDialog
    :title="$t('sheetExportCenter.upload.title')"
    width="600px"
    :btn1Loading="loading"
    :btn1Disabled="!formData.file || !formData.retentionAt"
    @close="$emit('close')"
    @confirm="onConfirm"
  >
    <!-- 檔案選擇（自訂 UI，不在 BobForm fields 內） -->
    <div class="form-item-top mb-[22px]">
      <p class="form-item-label">{{ $t('sheetExportCenter.upload.file') }} <span class="text-red-500">*</span></p>
      <div
        class="drop-zone"
        @click="fileInputRef.click()"
        @drop.prevent="onDrop"
        @dragenter.prevent
        @dragover.prevent
        @dragleave.prevent
      >
        <div v-if="!formData.file" class="flex flex-col items-center justify-center gap-2 py-4">
          <span class="material-icons text-gray-400" style="font-size: 40px">cloud_upload</span>
          <div class="text-sm text-gray-500">
            {{ $t('sheetExportCenter.upload.file') }}
          </div>
          <div class="text-xs text-gray-400">支援 .xlsx, .xls, .csv</div>
        </div>
        <div v-else class="flex flex-col items-center justify-center gap-2 py-4">
          <span class="material-icons text-green-500" style="font-size: 40px">check_circle</span>
          <div class="flex items-center gap-2">
            <span class="text-sm text-primary-100">{{ formData.file.name }}</span>
            <span
              class="material-icons cursor-pointer text-gray-400 hover:text-red-500"
              style="font-size: 18px"
              @click.stop="clearFile"
            >delete</span>
          </div>
        </div>
        <input
          ref="fileInputRef"
          type="file"
          accept=".xlsx,.xls,.csv"
          class="file-input-hidden"
          @change="onFileInput"
        >
      </div>
    </div>

    <!-- 保留期限 & 備註（使用 BobForm） -->
    <BobForm
      :formRef.sync="formRef"
      :fields="displayFields"
      :model="formData"
      label-position="top"
      :rules="formRules"
    />

    <!-- 上傳進度 -->
    <div v-if="loading" class="mt-[12px]">
      <el-progress :percentage="uploadProgress" :status="uploadProgress === 100 ? 'success' : null" />
      <div class="text-sm text-gray-500 mt-1">{{ $t('sheetExportCenter.upload.uploading') }}</div>
    </div>
  </BaseDialog>
</template>

<script>
import BaseDialog from '@/components/Dialog/BaseDialog.vue'
import BobForm from '@/components/BobForm.vue'
import { GetTempUploadLink, UploadFileToS3, CreateCompletedTask } from '@/api/sheetExportCenter'
import { defineComponent, computed, provide, ref } from 'vue'
import { useShop } from '@/use/shop'
import { useBaseForm } from '@/use/useForm'
import { i18n } from '@/plugins/i18n/i18n'
import { noEmptyRules } from '@/validation'
import dayjs from 'dayjs'

export default defineComponent({
  name: 'UploadDialog',
  components: { BaseDialog, BobForm },
  emits: ['close', 'refresh'],
  setup (props, { emit }) {
    const { shopId } = useShop()
    const { formData, formRef, initFormData, checkForm, loading } = useBaseForm()
    const fileInputRef = ref(null)
    const uploadProgress = ref(0)

    initFormData({
      file: null,
      retentionAt: null,
      note: '',
    })

    provide('formData', formData)

    const retentionPickerOptions = computed(() => ({
      disabledDate (date) {
        const today = dayjs().startOf('day')
        const maxDate = today.add(90, 'day')
        return dayjs(date).isBefore(today) || dayjs(date).isAfter(maxDate)
      },
    }))

    const displayFields = computed(() => [
      // 保留期限
      {
        prop: 'retentionAt',
        label: `${i18n.t('sheetExportCenter.upload.retentionDate')} (${i18n.t('sheetExportCenter.upload.maxRetentionDays')})`,
        body: {
          component: 'el-date-picker',
          props: {
            type: 'datetime',
            placeholder: i18n.t('sheetExportCenter.upload.retentionDate'),
            pickerOptions: retentionPickerOptions.value,
            defaultTime: '23:59:59',
          },
        },
      },
      // 備註
      {
        prop: 'note',
        label: i18n.t('sheetExportCenter.upload.note'),
        body: {
          component: 'BaseElInput',
          props: {
            type: 'textarea',
            rows: 3,
            maxlength: 200,
            showWordLimit: true,
          },
        },
      },
    ])

    const formRules = computed(() => ({
      retentionAt: [noEmptyRules()],
    }))

    const clearFile = () => {
      formData.file = null
      if (fileInputRef.value) {
        fileInputRef.value.type = 'text'
        fileInputRef.value.type = 'file'
      }
    }

    const onDrop = (e) => {
      const file = e.dataTransfer.files[0]
      if (file) formData.file = file
    }

    const onFileInput = (e) => {
      const file = e.target.files && e.target.files[0]
      if (file) formData.file = file
    }

    const onConfirm = async () => {
      if (loading.value) return

      if (!formData.file) {
        window.$message.error('請選擇要上傳的檔案')
        return
      }

      const isValid = await checkForm(formRef.value)
      if (!isValid) return

      loading.value = true
      uploadProgress.value = 0

      try {
        // 1. 取得臨時上傳連結
        const [linkRes, linkErr] = await GetTempUploadLink({
          shopId: shopId.value,
          contentType: formData.file.type,
        })
        if (linkErr) {
          window.$message.error(linkErr)
          return
        }

        // 2. 上傳檔案到 S3
        try {
          await UploadFileToS3({
            url: linkRes.url,
            file: formData.file,
            onUploadProgress: (progressEvent) => {
              uploadProgress.value = Math.round((progressEvent.loaded * 100) / progressEvent.total)
            },
          })
        } catch {
          window.$message.error(i18n.t('sheetExportCenter.upload.failed'))
          return
        }

        // 3. 建立完成任務
        const [, taskErr] = await CreateCompletedTask({
          shopId: shopId.value,
          objectKey: linkRes.objectKey,
          retentionAt: new Date(formData.retentionAt).toISOString(),
          fileName: formData.file.name,
          note: formData.note || undefined,
        })
        if (taskErr) {
          window.$message.error(taskErr)
          return
        }

        window.$message.success(i18n.t('sheetExportCenter.upload.success'))
        emit('refresh')
        emit('close')
      } finally {
        loading.value = false
      }
    }

    return {
      formData,
      formRef,
      loading,
      fileInputRef,
      uploadProgress,
      displayFields,
      formRules,
      clearFile,
      onDrop,
      onFileInput,
      onConfirm,
    }
  },
})
</script>

<style scoped lang="postcss">
.form-item-label {
  @apply text-sm font-medium text-gray-100 mb-[6px];
}

.drop-zone {
  @apply border-2 border-dashed border-gray-300 rounded-lg cursor-pointer min-h-[100px] flex items-center justify-center transition-colors;

  &:hover {
    @apply border-primary-100;
  }
}

.file-input-hidden {
  display: none;
}
</style>
